<template>
  <div class="admin-account-details detail-actions-page">
    <div class="header-actions detail-page-header detail-page-title-row">
      <button type="button" class="back-btn detail-page-back" @click="$router.push('/admin/accounts')">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        Back to Accounts
      </button>
      
      <div v-if="account" class="status-actions detail-page-actions">
        <button type="button" class="status-btn manage" @click="openBusinessEditor">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z"/>
          </svg>
          Edit business
        </button>
        <button type="button" class="status-btn manage" @click="showInvoiceLayout = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16v16H4z"/><path d="M4 9h16M9 9v11"/>
          </svg>
          Invoice layout
        </button>
        <button 
          v-if="account.status !== 'active'" 
          class="status-btn activate" 
          @click="updateStatus('active')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Activate
        </button>
        <button 
          v-if="account.status !== 'canceled'"
          class="status-btn cancel detail-page-action--danger"
          @click="updateStatus('canceled')"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          Canceled
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="admin-spinner"></div>
      <p>Loading details...</p>
    </div>

    <div v-else-if="account" class="content-grid">
      <!-- Left Column: Info & Stats -->
      <div class="left-col">
        <div class="widget info-card">
          <div class="widget-header">
            <h3>Business Information</h3>
            <span class="status-badge" :class="account.status || 'unknown'">
              {{ account.status || 'Unknown' }}
            </span>
          </div>
          <div class="widget-body">
            <div class="info-row">
              <span class="label">Name</span>
              <span class="value">{{ account.name }}</span>
            </div>
            <div class="info-row">
              <span class="label">Email</span>
              <span class="value">{{ account.email }}</span>
            </div>
            <div class="info-row">
              <span class="label">Phone</span>
              <span class="value">{{ account.owner_phone || account.phone || '-' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Currency</span>
              <span class="value">{{ account.currency || 'DH' }}</span>
            </div>
            <div class="info-row">
              <span class="label">Joined</span>
              <span class="value">{{ formatDate(account.created_at) }}</span>
            </div>
          </div>
        </div>

        <div class="widget stats-card">
          <div class="widget-header">
            <h3>Usage Stats</h3>
            <span class="usage-total">{{ usageStats.length }} metrics</span>
          </div>
          <div class="widget-body grid-stats">
            <div v-for="stat in usageStats" :key="stat.key" class="stat-box" :class="stat.tone">
              <span class="stat-icon">
                <AppIcon :name="stat.icon" :class="['bi', stat.icon]" />
              </span>
              <div>
                <div class="stat-num">{{ formatCount(stat.value) }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="widget billing-card">
          <div class="widget-header">
            <h3>Billing & Subscription</h3>
            <span class="plan-badge">{{ account.plan_id || 'Free' }}</span>
          </div>
          <div class="widget-body">
            <div class="plan-control">
              <label>Pricing Plan</label>
              <select v-model="selectedPlan" :disabled="planSaving" @change="updatePlan">
                <option v-for="plan in pricingPlans" :key="plan.key" :value="plan.key">
                  {{ plan.name }}
                </option>
              </select>
            </div>

            <div class="billing-date-form">
              <div class="billing-field">
                <label>Subscription Start</label>
                <input v-model="billingForm.start_date" type="date" :disabled="billingSaving">
              </div>
              <div class="billing-field">
                <label>Subscription End</label>
                <input v-model="billingForm.end_date" type="date" :disabled="billingSaving">
              </div>
              <button class="billing-save-btn" :disabled="billingSaving" @click="saveBillingDates">
                {{ billingSaving ? 'Saving...' : 'Save subscription dates' }}
              </button>
            </div>
          </div>
          <div class="widget-body" v-if="billing">
            <div class="info-row">
              <span class="label">Status</span>
              <span class="value" :class="billing.status === 'active' ? 'text-success' : 'text-danger'">
                {{ billing.status }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Starts On</span>
              <span class="value">{{ formatDate(billing.start_date) }}</span>
            </div>
            <div class="info-row">
              <span class="label">Ends On</span>
              <span class="value">{{ formatDate(billing.end_date) }}</span>
            </div>
            <div class="info-row">
              <span class="label">Price</span>
              <span class="value">${{ billing.price || 0 }}</span>
            </div>
            <div class="info-row" v-if="billing.subscription_id">
              <span class="label">Sub ID</span>
              <span class="value text-mono">{{ billing.subscription_id }}</span>
            </div>
          </div>
          <div class="widget-body empty" v-else>
            No billing record yet. Saving dates will create one.
          </div>

          <div class="widget-body invoice-admin-section">
            <div class="section-title">Add invoice</div>
            <div class="billing-field">
              <label>Invoice number</label>
              <input v-model.trim="invoiceForm.invoice_number" type="text" placeholder="e.g. INV-2026-001">
            </div>
            <div class="invoice-form-grid">
              <div class="billing-field">
                <label>Invoice date</label>
                <input v-model="invoiceForm.invoice_date" type="date">
              </div>
              <div class="billing-field">
                <label>Amount</label>
                <input v-model="invoiceForm.amount" type="number" min="0" step="0.01" placeholder="0.00">
              </div>
            </div>
            <div class="billing-field">
              <label>PDF file (max. 10 MB)</label>
              <input ref="invoiceFile" type="file" accept="application/pdf,.pdf" @change="selectInvoiceFile">
            </div>
            <button class="billing-save-btn" :disabled="invoiceUploading || !selectedInvoiceFile" @click="uploadInvoice">
              {{ invoiceUploading ? 'Uploading...' : 'Add invoice' }}
            </button>
          </div>

          <div v-if="billingInvoices.length" class="invoice-admin-list">
            <div v-for="invoice in billingInvoices" :key="invoice.id" class="invoice-admin-row">
              <div>
                <div class="invoice-admin-name">{{ invoice.invoice_number || invoice.file_name }}</div>
                <div class="invoice-admin-meta">
                  {{ formatDate(invoice.invoice_date) }}
                  <span v-if="invoice.amount !== null && invoice.amount !== undefined"> · {{ invoice.amount }} {{ account.currency || 'DH' }}</span>
                </div>
              </div>
              <button class="invoice-download-btn" @click="downloadInvoice(invoice)">Download</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Users List -->
      <div class="right-col">
        <div class="widget users-card">
          <div class="widget-header">
            <h3>Users ({{ users.length }})</h3>
          </div>
          <div class="widget-body p-0">
            <div class="table-responsive">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in users" :key="user.id">
                    <td>
                      <div class="user-name">{{ user.name }}</div>
                      <div class="user-email">{{ user.email }}</div>
                    </td>
                    <td class="member-role-cell">
                      <div v-if="user.role === 'owner'" class="member-access-summary owner-access">
                        <span class="role-badge owner">Owner</span>
                        <small>Full access</small>
                      </div>
                      <div v-else class="member-role-editor">
                        <div class="member-access-copy">
                          <strong>{{ user.role === 'admin' ? 'Admin access' : 'Limited access' }}</strong>
                          <small>{{ user.role === 'admin' ? 'Manage assigned modules' : 'Uses assigned permissions' }}</small>
                        </div>
                        <label class="member-role-switch" :title="user.role === 'admin' ? 'Change to limited access' : 'Change to admin access'">
                          <input
                            type="checkbox"
                            :checked="user.role === 'admin'"
                            :disabled="memberRoleSaving === user.id"
                            @change="updateMemberRole(user, $event.target.checked ? 'admin' : 'staff')"
                          >
                          <span></span>
                        </label>
                      </div>
                    </td>
                    <td>{{ formatDateTime(user.last_active) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="widget modules-card">
          <div class="widget-header">
            <h3>Modules</h3>
            <span class="module-count">{{ enabledModulesCount }}/{{ modules.length }} active</span>
          </div>
          <div class="widget-body modules-catalog-body">
            <div v-if="modules.length" class="modules-catalog">
              <div class="modules-catalog-controls">
                <div class="module-search-box">
                  <AppIcon name="bi-search" class="bi bi-search" />
                  <input v-model.trim="moduleSearch" type="search" placeholder="Search modules...">
                </div>
                <div class="module-category-tabs" aria-label="Module categories">
                  <button
                    v-for="category in moduleCategories"
                    :key="category.id"
                    type="button"
                    :class="{ active: moduleCategory === category.id }"
                    @click="moduleCategory = category.id"
                  >
                    {{ category.label }}
                  </button>
                </div>
              </div>

              <div v-if="filteredAdminModules.length" class="modules-catalog-grid">
                <article
                  v-for="module in filteredAdminModules"
                  :key="module.id"
                  class="catalog-module-card"
                  :class="{ enabled: module.enabled, disabled: !module.enabled && module.module_key !== 'settings' }"
                >
                  <div v-if="module.enabled" class="installed-ribbon"><span>✓ Installed</span></div>

                  <div class="catalog-module-main">
                    <div class="catalog-module-icon" :style="{ backgroundColor: moduleColor(module.module_key) }">
                      <AppIcon :name="module.icon || 'bi-grid'" :class="module.icon || 'bi-grid'" />
                    </div>
                    <div class="catalog-module-copy">
                      <h4>{{ moduleTitle(module) }}</h4>
                      <p>{{ moduleDescription(module) }}</p>
                    </div>
                  </div>

                  <div class="catalog-module-footer">
                    <span class="submodule-count">
                      {{ enabledSubModulesCount(module) }}/{{ (module.items || []).length }} features
                    </span>
                    <button
                      v-if="module.module_key !== 'settings'"
                      type="button"
                      class="module-action-button"
                      :class="module.enabled ? 'deactivate' : 'activate'"
                      :disabled="moduleSaving === module.id"
                      @click="toggleModule(module, !module.enabled)"
                    >
                      {{ moduleSaving === module.id ? 'Saving...' : (module.enabled ? 'Deactivate' : 'Activate') }}
                    </button>
                    <span v-else class="core-module-badge">Core</span>
                  </div>

                  <div v-if="module.items && module.items.length" class="catalog-submodules">
                    <button
                      v-for="item in sortedModuleItems(module.items)"
                      :key="item.id"
                      type="button"
                      class="catalog-submodule-pill"
                      :class="{ active: item.enabled }"
                      :title="subModuleDescription(module, item)"
                      :disabled="itemSaving === item.id || module.module_key === 'settings'"
                      @click="toggleModuleItem(module, item, !item.enabled)"
                    >
                      <AppIcon
                        :name="item.enabled ? 'bi-toggle-on' : 'bi-toggle-off'"
                        :class="item.enabled ? 'bi bi-toggle-on' : 'bi bi-toggle-off'"
                      />
                      {{ subModuleTitle(item) }}
                    </button>
                  </div>
                </article>
              </div>

              <div v-else class="modules-empty-state">
                <AppIcon name="bi-search" class="bi bi-search" />
                <strong>No modules found</strong>
                <span>Try another search or category.</span>
                <button type="button" @click="moduleSearch = ''; moduleCategory = 'all'">Clear filters</button>
              </div>
            </div>
            <div class="empty" v-else>No modules configured for this account.</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showBusinessEditor" class="admin-editor-overlay" @click.self="closeBusinessEditor">
      <form class="admin-editor-modal business-editor" @submit.prevent="saveBusiness">
        <div class="admin-editor-header">
          <div>
            <h3>Edit business data</h3>
            <p>Changes apply immediately to the customer account and its documents.</p>
          </div>
          <button type="button" class="editor-close" aria-label="Close" :disabled="businessSaving" @click="closeBusinessEditor">×</button>
        </div>

        <div class="admin-editor-body">
          <div class="business-form-grid">
            <label class="editor-field">
              <span>Business name *</span>
              <input v-model.trim="businessForm.name" type="text" maxlength="255" required>
            </label>
            <label class="editor-field">
              <span>Email</span>
              <input v-model.trim="businessForm.email" type="email" maxlength="255">
            </label>
            <label class="editor-field">
              <span>Phone</span>
              <input v-model.trim="businessForm.phone" type="text" maxlength="255">
            </label>
            <label class="editor-field">
              <span>Website</span>
              <input v-model.trim="businessForm.website" type="text" maxlength="255" placeholder="https://example.com">
            </label>
            <label class="editor-field full-width">
              <span>Address</span>
              <input v-model.trim="businessForm.address" type="text" maxlength="255">
            </label>
            <label class="editor-field">
              <span>Currency</span>
              <select v-model="businessForm.currency">
                <option value="">No currency</option>
                <option v-for="currency in currencyOptions" :key="currency.value" :value="currency.value">
                  {{ currency.label }}
                </option>
              </select>
            </label>
            <label class="editor-field">
              <span>VAT rate (%)</span>
              <input v-model="businessForm.vat" type="number" min="0" max="100" step="0.01">
            </label>
            <label class="editor-field full-width">
              <span>Logo URL</span>
              <input v-model.trim="businessForm.logo" type="text" maxlength="255" placeholder="https://...">
            </label>
            <label class="editor-field full-width">
              <span>Electronic stamp URL</span>
              <input v-model.trim="businessForm.electronic_stamp" type="text" maxlength="255" placeholder="https://...">
            </label>
            <label class="editor-field full-width">
              <span>Banking information</span>
              <textarea v-model="businessForm.billing_info" rows="3" maxlength="5000" placeholder="RIB, SWIFT, bank details..."></textarea>
            </label>
            <label class="editor-field full-width">
              <span>Payment terms</span>
              <textarea v-model="businessForm.payment_term" rows="3" maxlength="2000"></textarea>
            </label>
          </div>
          <div v-if="businessError" class="editor-error">{{ businessError }}</div>
        </div>

        <div class="admin-editor-footer">
          <button type="button" class="editor-secondary" :disabled="businessSaving" @click="closeBusinessEditor">Cancel</button>
          <button type="submit" class="editor-primary" :disabled="businessSaving">
            {{ businessSaving ? 'Saving...' : 'Save business data' }}
          </button>
        </div>
      </form>
    </div>

    <div v-if="showInvoiceLayout" class="admin-editor-overlay invoice-layout-overlay">
      <div class="admin-editor-modal invoice-layout-modal">
        <div class="admin-editor-header">
          <div>
            <h3>Invoice layout · {{ account.name }}</h3>
            <p>Select a preset or customize the layout without signing into this account.</p>
          </div>
          <button type="button" class="editor-close" aria-label="Close" @click="showInvoiceLayout = false">×</button>
        </div>
        <div class="invoice-layout-body">
          <invoice-template-picker
            :admin-account-id="String($route.params.id)"
            :business-override="account"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { PRICING_PLANS, DEFAULT_PLAN_KEY } from '@/constants/pricingPlans'
import { CURRENCY_OPTIONS } from '@/constants/currencies'
import InvoiceTemplatePicker from '@/components/InvoiceTemplatePicker.vue'

export default {
  name: 'AdminAccountDetails',
  components: { InvoiceTemplatePicker },
  data() {
    return {
      account: null,
      users: [],
      billing: null,
      billingInvoices: [],
      billingForm: { start_date: '', end_date: '' },
      billingSaving: false,
      invoiceForm: {
        invoice_number: '',
        invoice_date: moment().format('YYYY-MM-DD'),
        amount: ''
      },
      selectedInvoiceFile: null,
      invoiceUploading: false,
      modules: [],
      selectedPlan: DEFAULT_PLAN_KEY,
      planSaving: false,
      moduleSaving: null,
      itemSaving: null,
      memberRoleSaving: null,
      moduleSearch: '',
      moduleCategory: 'all',
      moduleCategories: [
        { id: 'all', label: 'All' },
        { id: 'operations', label: 'Operations' },
        { id: 'crm', label: 'Growth & CRM' },
        { id: 'planning', label: 'HR & Planning' },
        { id: 'finance', label: 'Finance' },
        { id: 'system', label: 'System' }
      ],
      usage: {},
      loading: true,
      showBusinessEditor: false,
      showInvoiceLayout: false,
      businessSaving: false,
      businessError: '',
      businessForm: {
        name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        currency: '',
        vat: '',
        billing_info: '',
        payment_term: '',
        logo: '',
        electronic_stamp: ''
      }
    }
  },
  computed: {
    currencyOptions() {
      return CURRENCY_OPTIONS
    },
    pricingPlans() {
      return PRICING_PLANS
    },
    enabledModulesCount() {
      return this.modules.filter(module => module.enabled).length
    },
    usageStats() {
      return [
        { key: 'invoices', label: 'Invoices', icon: 'bi-receipt', tone: 'blue', value: this.usage.invoices },
        { key: 'quotes', label: 'Quotes', icon: 'bi-file-text', tone: 'indigo', value: this.usage.quotes },
        { key: 'orders', label: 'Orders', icon: 'bi-cart3', tone: 'violet', value: this.usage.orders },
        { key: 'deliveries', label: 'Deliveries', icon: 'bi-truck', tone: 'cyan', value: this.usage.deliveries },
        { key: 'receipts', label: 'Receipts', icon: 'bi-file-earmark-check', tone: 'teal', value: this.usage.receipts },
        { key: 'products', label: 'Products', icon: 'bi-box-seam', tone: 'red', value: this.usage.products },
        { key: 'warehouses', label: 'Warehouses', icon: 'bi-building', tone: 'slate', value: this.usage.warehouses },
        { key: 'clients', label: 'Clients', icon: 'bi-people', tone: 'green', value: this.usage.clients },
        { key: 'suppliers', label: 'Suppliers', icon: 'bi-person-badge', tone: 'amber', value: this.usage.suppliers },
        { key: 'purchases', label: 'Purchases', icon: 'bi-bag', tone: 'orange', value: this.usage.purchases },
        { key: 'transactions', label: 'Transactions', icon: 'bi-arrow-left-right', tone: 'emerald', value: this.usage.transactions },
        { key: 'projects', label: 'Projects', icon: 'bi-kanban', tone: 'purple', value: this.usage.projects },
        { key: 'employees', label: 'Employees', icon: 'bi-person-workspace', tone: 'rose', value: this.usage.employees },
        { key: 'subscriptions', label: 'Subscriptions', icon: 'bi-calendar-check', tone: 'lime', value: this.usage.subscriptions },
        { key: 'members', label: 'Members', icon: 'bi-person-check', tone: 'navy', value: this.usage.members }
      ]
    },
    filteredAdminModules() {
      let filtered = [...this.modules]
      const categories = {
        operations: ['sales', 'pos', 'inventory', 'purchases'],
        crm: ['crm', 'leads', 'marketing', 'dashboard', 'advanced_insights'],
        planning: ['hr', 'planning', 'project_management', 'reservations'],
        finance: ['finance', 'accountant', 'subscription_management'],
        system: ['settings', 'attachments', 'ai', 'contracts', 'importer']
      }

      if (this.moduleCategory !== 'all') {
        filtered = filtered.filter(module => (categories[this.moduleCategory] || []).includes(module.module_key))
      }

      const query = this.moduleSearch.toLowerCase()
      if (query) {
        filtered = filtered.filter(module => [
          this.moduleTitle(module),
          this.moduleDescription(module),
          module.module_key
        ].some(value => String(value || '').toLowerCase().includes(query)))
      }

      return filtered.sort((a, b) => {
        if (a.module_key === 'settings') return 1
        if (b.module_key === 'settings') return -1
        return Number(a.position || 0) - Number(b.position || 0)
      })
    }
  },
  created() {
    this.fetchDetails()
  },
  methods: {
    async updateMemberRole(user, role) {
      if (!user || user.role === 'owner' || user.role === role) return

      const previousRole = user.role
      user.role = role
      this.memberRoleSaving = user.id
      try {
        const response = await axios.patch(
          `${process.env.VUE_APP_API}/admin/accounts/${this.$route.params.id}/members/${user.id}/role`,
          { role }
        )
        user.role = response.data.member?.role || role
      } catch (error) {
        user.role = previousRole
        alert(error.response?.data?.message || 'Failed to update member role')
      } finally {
        this.memberRoleSaving = null
      }
    },
    openBusinessEditor() {
      Object.keys(this.businessForm).forEach(field => {
        this.businessForm[field] = this.account[field] === null || this.account[field] === undefined
          ? ''
          : this.account[field]
      })
      this.businessError = ''
      this.showBusinessEditor = true
    },
    closeBusinessEditor() {
      if (this.businessSaving) return
      this.showBusinessEditor = false
      this.businessError = ''
    },
    async saveBusiness() {
      this.businessSaving = true
      this.businessError = ''
      try {
        const payload = {
          ...this.businessForm,
          vat: this.businessForm.vat === '' ? null : Number(this.businessForm.vat),
          logo: this.businessForm.logo || null,
          electronic_stamp: this.businessForm.electronic_stamp || null
        }
        const response = await axios.patch(
          `${process.env.VUE_APP_API}/admin/accounts/${this.$route.params.id}`,
          payload
        )
        if (response.data.success) {
          this.account = { ...this.account, ...response.data.business }
          this.showBusinessEditor = false
        }
      } catch (error) {
        this.businessError = error.response?.data?.message || 'Failed to update business data.'
      } finally {
        this.businessSaving = false
      }
    },
    async fetchDetails() {
      this.loading = true
      try {
        const id = this.$route.params.id
        const res = await axios.get(`${process.env.VUE_APP_API}/admin/accounts/${id}`)
        if (res.data.success) {
          this.account = res.data.business
          this.users = res.data.users || []
          this.billing = res.data.billing
          this.billingInvoices = res.data.billingInvoices || []
          this.billingForm = {
            start_date: this.toDateInput(this.billing && this.billing.start_date),
            end_date: this.toDateInput(this.billing && this.billing.end_date)
          }
          this.modules = res.data.modules || []
          this.usage = res.data.usage || {}
          this.selectedPlan = this.normalizePlan(this.account.plan_id)
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.$router.push('/admin')
        }
      } finally {
        this.loading = false
      }
    },
    async updateStatus(newStatus) {
      if (!confirm(`Are you sure you want to change the status to ${newStatus.toUpperCase()}?`)) return
      
      try {
        const id = this.$route.params.id
        const res = await axios.patch(`${process.env.VUE_APP_API}/admin/accounts/${id}`, {
          status: newStatus
        })
        
        if (res.data.success) {
          this.account = res.data.business || { ...this.account, status: newStatus }
          alert(`Status updated to ${newStatus}`)
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update status')
      }
    },
    async updatePlan() {
      if (!this.account || this.selectedPlan === this.account.plan_id) return

      const oldPlan = this.normalizePlan(this.account.plan_id)
      if (!confirm(`Change account plan from ${oldPlan} to ${this.selectedPlan}?`)) {
        this.selectedPlan = oldPlan
        return
      }

      this.planSaving = true
      try {
        const id = this.$route.params.id
        const res = await axios.patch(`${process.env.VUE_APP_API}/admin/accounts/${id}`, {
          plan_id: this.selectedPlan
        })

        if (res.data.success) {
          this.account = res.data.business || { ...this.account, plan_id: this.selectedPlan }
          if (this.billing) this.billing.plan_id = this.selectedPlan
          alert(`Plan updated to ${this.selectedPlan}`)
        }
      } catch (error) {
        this.selectedPlan = oldPlan
        alert(error.response?.data?.message || 'Failed to update plan')
      } finally {
        this.planSaving = false
      }
    },
    async saveBillingDates() {
      if (this.billingForm.start_date && this.billingForm.end_date && this.billingForm.end_date < this.billingForm.start_date) {
        alert('End date must be on or after start date')
        return
      }

      this.billingSaving = true
      try {
        const id = this.$route.params.id
        const res = await axios.patch(`${process.env.VUE_APP_API}/admin/accounts/${id}/billing`, {
          start_date: this.billingForm.start_date || null,
          end_date: this.billingForm.end_date || null
        })
        this.billing = res.data.billing
        alert('Subscription dates updated')
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to update subscription dates')
      } finally {
        this.billingSaving = false
      }
    },
    selectInvoiceFile(event) {
      this.selectedInvoiceFile = event.target.files && event.target.files[0]
    },
    async uploadInvoice() {
      if (!this.selectedInvoiceFile) return

      this.invoiceUploading = true
      try {
        const form = new FormData()
        form.append('file', this.selectedInvoiceFile)
        form.append('invoice_number', this.invoiceForm.invoice_number || '')
        form.append('invoice_date', this.invoiceForm.invoice_date || '')
        form.append('amount', this.invoiceForm.amount === '' ? '' : this.invoiceForm.amount)

        const id = this.$route.params.id
        const res = await axios.post(`${process.env.VUE_APP_API}/admin/accounts/${id}/billing/invoices`, form)
        this.billingInvoices.unshift(res.data.invoice)
        this.invoiceForm = { invoice_number: '', invoice_date: moment().format('YYYY-MM-DD'), amount: '' }
        this.selectedInvoiceFile = null
        if (this.$refs.invoiceFile) this.$refs.invoiceFile.value = ''
        if (!this.billing) await this.fetchDetails()
        alert('Invoice added')
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to upload invoice')
      } finally {
        this.invoiceUploading = false
      }
    },
    async downloadInvoice(invoice) {
      try {
        const id = this.$route.params.id
        const response = await axios.get(
          `${process.env.VUE_APP_API}/admin/accounts/${id}/billing/invoices/${invoice.id}/download`,
          { responseType: 'blob' }
        )
        this.saveBlob(response.data, invoice.file_name || 'invoice.pdf')
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to download invoice')
      }
    },
    saveBlob(blob, fileName) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    toDateInput(date) {
      return date ? moment(date).format('YYYY-MM-DD') : ''
    },
    async toggleModule(module, enabled) {
      const oldValue = module.enabled
      module.enabled = enabled
      this.moduleSaving = module.id

      try {
        const id = this.$route.params.id
        await axios.patch(`${process.env.VUE_APP_API}/admin/accounts/${id}/modules/${module.id}`, {
          enabled
        })
      } catch (error) {
        module.enabled = oldValue
        alert(error.response?.data?.message || 'Failed to update module')
      } finally {
        this.moduleSaving = null
      }
    },
    async toggleModuleItem(module, item, enabled) {
      const oldValue = item.enabled
      item.enabled = enabled
      this.itemSaving = item.id

      try {
        const id = this.$route.params.id
        await axios.patch(`${process.env.VUE_APP_API}/admin/accounts/${id}/modules/${module.id}/items/${item.id}`, {
          enabled
        })
      } catch (error) {
        item.enabled = oldValue
        alert(error.response?.data?.message || 'Failed to update sub module')
      } finally {
        this.itemSaving = null
      }
    },
    sortedModuleItems(items = []) {
      return [...items].sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    },
    enabledSubModulesCount(module) {
      return (module.items || []).filter(item => item.enabled).length
    },
    moduleColor(moduleKey) {
      const colors = {
        dashboard: '#4a6cf7',
        crm: '#00a09d',
        inventory: '#d9363e',
        sales: '#7c7bad',
        purchases: '#875a7b',
        pos: '#f06292',
        finance: '#3eb4e4',
        attachments: '#607d8b',
        subscription_management: '#ff9800',
        reservations: '#4caf50',
        accountant: '#1976d2',
        project_management: '#9c27b0',
        hr: '#c44033',
        advanced_insights: '#3f51b5',
        contracts: '#795548',
        planning: '#008784',
        leads: '#e91e63',
        ai: '#6366f1',
        importer: '#0f766e',
        settings: '#333333'
      }
      return colors[moduleKey] || '#1a1a1a'
    },
    moduleTitle(module) {
      return this.translateLabel(module.title || module.module_key)
    },
    moduleDescription(module) {
      return this.moduleDescriptionMap()[module.module_key] || module.module_key || this.humanizeKey(module.description)
    },
    subModuleTitle(item) {
      return this.translateLabel(item.title)
    },
    subModuleDescription(module, item) {
      return this.subModuleDescriptionMap()[`${module.module_key}:${item.title}`] || item.permission || item.link || '-'
    },
    moduleDescriptionMap() {
      return {
        dashboard: 'dashboard',
        crm: 'crm',
        inventory: 'inventory',
        sales: 'sales',
        purchases: 'purchases',
        pos: 'pos',
        finance: 'finance',
        project_management: 'project_management',
        accountant: 'accountant',
        attachments: 'attachments',
        ai: 'ai',
        subscription_management: 'subscription_management',
        advanced_insights: 'advanced_insights',
        hr: 'hr',
        reservations: 'reservations',
        leads: 'leads',
        settings: 'settings',
        contracts: 'contracts',
        planning: 'planning',
        importer: 'importer'
      }
    },
    subModuleDescriptionMap() {
      return {
        'dashboard:dashboard': 'view_dashboard',
        'crm:clients': 'view_clients',
        'crm:suppliers': 'view_suppliers',
        'inventory:products': 'view_inventories',
        'inventory:categories': 'view_categories',
        'inventory:warehouses': 'view_warehouses',
        'sales:quotes': 'view_quotes',
        'sales:orders': 'view_orders',
        'sales:deliveries': 'view_deliveries',
        'sales:invoices': 'view_invoices',
        'sales:processes': 'view_receipts',
        'sales:returns': 'view_returns',
        'sales:sales_insights': 'view_invoices',
        'purchases:purchases': 'view_purchases',
        'purchases:supplier_receptions': 'view_receptions',
        'purchases:supplier_invoices': 'view_supplier_invoices',
        'purchases:supplier_returns': 'view_supplier_returns',
        'purchases:purchase_insights': 'view_purchases',
        'pos:pos': 'view_pos',
        'pos:pos_transactions': 'view_pos_transactions',
        'pos:pos_cash_registers': 'view_pos_cash_registers',
        'pos:pos_insights': 'view_pos_insights',
        'finance:payments': 'view_payments',
        'finance:bank_accounts': 'view_bank_accounts',
        'finance:zakat': 'view_payments',
        'project_management:projects': 'view_projects',
        'project_management:tasks': 'view_tasks',
        'accountant:accountants': 'view_accountants',
        'attachments:attachments': 'view_attachments',
        'ai:gstocky_ai': 'view_ai',
        'subscription_management:overview': 'view_subscriptions',
        'subscription_management:subscriptions': 'view_subscriptions',
        'subscription_management:plans': 'view_plans',
        'subscription_management:attendance': 'view_attendance',
        'subscription_management:external_attendance': 'view_attendance',
        'advanced_insights:advanced_insights': 'view_advanced_insights',
        'hr:my_space': 'view_my_space',
        'hr:employees': 'view_employees',
        'hr:hr_insights': 'view_hr_insights',
        'hr:employee_leaves': 'view_holidays',
        'hr:employee_documents': 'view_employee_documents',
        'hr:payroll_management': 'view_payrolls',
        'hr:attendance_management': 'view_attendances',
        'reservations:reservations': 'view_reservations',
        'reservations:reservation_insights': 'view_reservations',
        'leads:leads': 'view_leads',
        'settings:settings': 'owner_only',
        'contracts:contracts': 'view_contracts',
        'planning:plannings': 'view_plannings',
        'planning:my_planning': 'view_my_planning',
        'importer:importer': 'view_importer'
      }
    },
    translateLabel(value) {
      if (!value) return '-'

      const key = String(value)
      if (this.$te && this.$te(key)) return this.$t(key)

      const translated = this.$t ? this.$t(key) : key
      if (translated && translated !== key) return translated

      return this.humanizeKey(key)
    },
    humanizeKey(value) {
      const acronyms = ['ai', 'crm', 'hr', 'pos']
      return String(value)
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => acronyms.includes(word.toLowerCase())
          ? word.toUpperCase()
          : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(' ')
    },
    normalizePlan(planId) {
      const normalized = String(planId || '').toLowerCase()
      return this.pricingPlans.some(plan => plan.key === normalized) ? normalized : DEFAULT_PLAN_KEY
    },
    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('MMM D, YYYY')
    },
    formatDateTime(date) {
      if (!date) return 'Never'
      return moment(date).fromNow()
    },
    formatCount(value) {
      return new Intl.NumberFormat('en-US').format(Number(value) || 0)
    }
  }
}
</script>

<style scoped>
.admin-account-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
  max-width: 100%;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  transition: color 0.2s;
}

.back-btn:hover {
  color: #111827;
}

.status-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.status-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.status-btn:active {
  transform: scale(0.96);
}

.status-btn.cancel {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.status-btn.cancel:hover {
  background: #fee2e2;
}

.status-btn.activate {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.status-btn.activate:hover {
  background: #d1fae5;
}

.status-btn.manage {
  color: #4338ca;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
}

.status-btn.manage:hover {
  background: #e0e7ff;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  color: #64748b;
  gap: 16px;
}

.admin-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.content-grid {
  display: grid;
  grid-template-columns: 350px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

@media (max-width: 900px) {
  .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 600px) {
  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .status-actions {
    flex-direction: column;
  }
  
  .status-btn {
    justify-content: center;
    width: 100%;
  }

  .grid-stats {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .widget-body {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .grid-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.left-col {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.right-col {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}

.widget {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.widget-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.widget-header h3 {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.widget-body {
  padding: 20px;
}

.widget-body.p-0 {
  padding: 0;
}

.plan-control {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.plan-control label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.plan-control select {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  outline: none;
}

.plan-control select:disabled {
  opacity: 0.65;
  cursor: wait;
}

.billing-date-form,
.invoice-admin-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e5e7eb;
}

.billing-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.billing-field label,
.section-title {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.billing-field input {
  width: 100%;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 13px;
  box-sizing: border-box;
}

.invoice-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
}

.billing-save-btn,
.invoice-download-btn {
  border: 0;
  border-radius: 8px;
  background: #4f46e5;
  color: #fff;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.billing-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.invoice-admin-list {
  border-top: 1px solid #e5e7eb;
}

.invoice-admin-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.invoice-admin-row:last-child {
  border-bottom: 0;
}

.invoice-admin-name {
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.invoice-admin-meta {
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
}

.invoice-download-btn {
  flex: 0 0 auto;
  padding: 7px 10px;
  background: #eef2ff;
  color: #4338ca;
}

.module-count {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.modules-catalog-body { padding: 18px; background: #f8fafc; }
.modules-catalog { display: flex; flex-direction: column; gap: 18px; }
.modules-catalog-controls { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.module-search-box { position: relative; flex: 1; min-width: 190px; max-width: 330px; }
.module-search-box .bi { position: absolute; top: 50%; left: 12px; color: #94a3b8; transform: translateY(-50%); }
.module-search-box input {
  width: 100%;
  height: 38px;
  padding: 8px 12px 8px 35px;
  box-sizing: border-box;
  color: #334155;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 12px;
}
.module-search-box input:focus { border-color: #94a3b8; box-shadow: 0 0 0 3px rgba(148, 163, 184, .14); }
.module-category-tabs { display: flex; max-width: 100%; overflow-x: auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 6px rgba(15, 23, 42, .04); }
.module-category-tabs button {
  flex: 0 0 auto;
  height: 36px;
  padding: 0 11px;
  color: #64748b;
  background: #fff;
  border: 0;
  border-right: 1px solid #f1f5f9;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}
.module-category-tabs button:last-child { border-right: 0; }
.module-category-tabs button.active { color: #fff; background: #4a4a4a; }
.modules-catalog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.catalog-module-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 12px;
  box-shadow: 0 3px 10px rgba(15, 23, 42, .07);
  transition: transform .2s ease, box-shadow .2s ease, opacity .2s ease, filter .2s ease;
}
.catalog-module-card:hover { transform: translateY(-3px); box-shadow: 0 14px 24px rgba(15, 23, 42, .11); }
.catalog-module-card.enabled { border-color: rgba(74, 74, 74, .16); box-shadow: 0 0 0 1px rgba(74, 74, 74, .08), 0 8px 18px rgba(15, 23, 42, .08); }
.catalog-module-card.disabled { opacity: .58; filter: grayscale(.55); }
.catalog-module-card.disabled:hover { opacity: .86; filter: grayscale(.2); }
.installed-ribbon { position: absolute; top: 0; left: 0; width: 72px; height: 72px; overflow: hidden; pointer-events: none; z-index: 1; }
.installed-ribbon span {
  position: absolute;
  top: 10px;
  left: -22px;
  width: 80px;
  padding: 3px 0;
  color: #fff;
  background: #198754;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .15);
  font-size: 7px;
  font-weight: 800;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(-45deg);
}
.catalog-module-main { display: flex; align-items: flex-start; gap: 13px; min-height: 112px; padding: 34px 16px 15px; }
.catalog-module-icon {
  flex: 0 0 52px;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  color: #fff;
  border-radius: 9px;
  box-shadow: 0 4px 8px rgba(15, 23, 42, .12);
  font-size: 24px;
  transition: transform .2s ease;
}
.catalog-module-card:hover .catalog-module-icon { transform: scale(1.05); }
.catalog-module-copy { min-width: 0; }
.catalog-module-copy h4 { margin: 2px 0 5px; overflow: hidden; color: #0f172a; font-size: 14px; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
.catalog-module-copy p { display: -webkit-box; margin: 0; overflow: hidden; color: #64748b; font-size: 10px; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.catalog-module-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 0 16px 14px; }
.submodule-count { color: #94a3b8; font-size: 10px; font-weight: 650; }
.module-action-button {
  padding: 7px 12px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 750;
  cursor: pointer;
  transition: background .2s ease, color .2s ease;
}
.module-action-button.activate { color: #fff; background: #1f2937; border: 1px solid #1f2937; }
.module-action-button.deactivate { color: #b91c1c; background: #fff; border: 1px solid #fecaca; }
.module-action-button.deactivate:hover { background: #fef2f2; }
.module-action-button:disabled { opacity: .55; cursor: wait; }
.core-module-badge { padding: 6px 10px; color: #475569; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 5px; font-size: 9px; font-weight: 800; text-transform: uppercase; }
.catalog-submodules { display: flex; flex-wrap: wrap; gap: 6px; padding: 10px 12px; background: #f8fafc; border-top: 1px solid #eef2f7; }
.catalog-submodule-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 4px 7px;
  overflow: hidden;
  color: #64748b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, .04);
  font-size: 9px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
}
.catalog-submodule-pill:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 8px rgba(15, 23, 42, .08); }
.catalog-submodule-pill.active { color: #047857; border-color: #a7f3d0; background: #ecfdf5; }
.catalog-submodule-pill .bi { flex: 0 0 auto; font-size: 12px; }
.catalog-submodule-pill:disabled { cursor: not-allowed; opacity: .7; }
.modules-empty-state { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 50px 20px; color: #94a3b8; text-align: center; }
.modules-empty-state > .bi { margin-bottom: 4px; font-size: 28px; }.modules-empty-state strong { color: #475569; font-size: 13px; }.modules-empty-state span { font-size: 11px; }
.modules-empty-state button { color: #334155; background: none; border: 0; font-size: 11px; font-weight: 700; text-decoration: underline; cursor: pointer; }

@media (max-width: 1180px) {
  .modules-catalog-controls { align-items: stretch; flex-direction: column; }
  .module-search-box { max-width: none; }
}

@media (max-width: 720px) {
  .modules-catalog-grid { grid-template-columns: 1fr; }
  .modules-catalog-body { padding: 12px; }
}

.module-list {
  display: flex;
  flex-direction: column;
}

.module-block {
  border-bottom: 1px solid #e5e7eb;
}

.module-block:last-child {
  border-bottom: none;
}

.module-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  background: #ffffff;
}

.module-identity,
.submodule-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.module-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 17px;
  transition: color 0.2s, background-color 0.2s;
}

.module-icon.active {
  background: #eef2ff;
  color: #4f46e5;
}

.module-meta,
.submodule-meta {
  min-width: 0;
}

.module-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.module-key {
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.submodule-summary {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid #cbd5e1;
  color: #94a3b8;
}

.submodule-list {
  padding: 4px 20px 12px 34px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.submodule-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.submodule-row:last-child {
  border-bottom: none;
}

.submodule-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 8px;
  background: #e9eef5;
  color: #94a3b8;
  font-size: 13px;
  transition: color 0.2s, background-color 0.2s;
}

.submodule-icon.active {
  background: #e0e7ff;
  color: #4f46e5;
}

.submodule-title {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.submodule-key {
  color: #94a3b8;
  font-size: 11px;
  margin-top: 2px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex: 0 0 auto;
}

.switch.compact {
  width: 38px;
  height: 21px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #cbd5e1;
  border: 1px solid #cbd5e1;
  transition: 0.2s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 2px;
  top: 2px;
  background: #f8fafc;
  transition: 0.2s;
  border-radius: 50%;
}

.switch input:checked + .slider {
  background: rgba(16, 185, 129, 0.45);
  border-color: rgba(16, 185, 129, 0.6);
}

.switch input:checked + .slider:before {
  transform: translateX(20px);
}

.switch.compact .slider:before {
  width: 15px;
  height: 15px;
}

.switch.compact input:checked + .slider:before {
  transform: translateX(17px);
}

.switch input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.55;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-row:first-child {
  padding-top: 0;
}

.info-row .label {
  color: #64748b;
  font-size: 13px;
}

.info-row .value {
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
}

.grid-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}

.stat-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  padding: 11px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.stat-icon {
  flex: 0 0 32px;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  color: #4f46e5;
  background: #eef2ff;
  border-radius: 8px;
}

.stat-icon .bi {
  width: 15px;
  height: 15px;
}

.stat-num {
  font-size: 17px;
  line-height: 1;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  margin-top: 4px;
}

.usage-total { color: #94a3b8; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.stat-box.green .stat-icon, .stat-box.emerald .stat-icon { color: #047857; background: #ecfdf5; }
.stat-box.blue .stat-icon, .stat-box.cyan .stat-icon, .stat-box.navy .stat-icon { color: #2563eb; background: #eff6ff; }
.stat-box.indigo .stat-icon, .stat-box.violet .stat-icon, .stat-box.purple .stat-icon { color: #6d28d9; background: #f5f3ff; }
.stat-box.red .stat-icon, .stat-box.rose .stat-icon { color: #be123c; background: #fff1f2; }
.stat-box.amber .stat-icon, .stat-box.orange .stat-icon { color: #b45309; background: #fffbeb; }
.stat-box.teal .stat-icon, .stat-box.lime .stat-icon { color: #0f766e; background: #f0fdfa; }
.stat-box.slate .stat-icon { color: #475569; background: #f1f5f9; }

.admin-table {
  width: 100%;
  border-collapse: collapse;
}

.admin-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}

.admin-table td {
  padding: 16px 20px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.admin-table tr:hover td {
  background: #f8fafc;
}

.user-name {
  font-weight: 600;
  color: #111827;
}

.user-email {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.role-badge {
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  color: #475569;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.role-badge.owner { background: rgba(245, 158, 11, 0.1); color: #fbbf24; border-color: rgba(245, 158, 11, 0.2); }
.role-badge.admin { background: rgba(59, 130, 246, 0.1); color: #60a5fa; border-color: rgba(59, 130, 246, 0.2); }

.member-role-cell { min-width: 245px; }
.member-role-editor { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.member-access-copy { min-width: 0; }
.member-access-copy strong { display: block; color: #334155; font-size: 12px; font-weight: 700; }
.member-access-copy small { display: block; margin-top: 3px; color: #94a3b8; font-size: 10px; }
.member-access-summary { display: flex; align-items: center; gap: 8px; }
.member-access-summary small { color: #94a3b8; font-size: 10px; }
.member-role-switch { position: relative; flex: 0 0 42px; width: 42px; height: 23px; margin: 0; }
.member-role-switch input { position: absolute; width: 1px; height: 1px; opacity: 0; }
.member-role-switch span { position: absolute; inset: 0; background: #cbd5e1; border-radius: 999px; cursor: pointer; transition: background .2s ease; }
.member-role-switch span::before { position: absolute; top: 3px; left: 3px; width: 17px; height: 17px; content: ''; background: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(15, 23, 42, .2); transition: transform .2s ease; }
.member-role-switch input:checked + span { background: #4f46e5; }
.member-role-switch input:checked + span::before { transform: translateX(19px); }
.member-role-switch input:focus-visible + span { outline: 3px solid rgba(99, 102, 241, .25); outline-offset: 2px; }
.member-role-switch input:disabled + span { cursor: wait; opacity: .6; }

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.active { background: rgba(16, 185, 129, 0.15); color: #34d399; }
.status-badge.canceled { background: rgba(239, 68, 68, 0.15); color: #f87171; }
.status-badge.inactive { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
.status-badge.trial { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
.status-badge.null, .status-badge.unknown { background: rgba(148, 163, 184, 0.15); color: #94a3b8; }

.plan-badge { padding: 4px 8px; background: rgba(99, 102, 241, 0.1); border: 1px solid rgba(99, 102, 241, 0.2); color: #818cf8; border-radius: 6px; font-size: 11px; font-weight: 600; }
.text-success { color: #34d399 !important; }
.text-danger { color: #f87171 !important; }
.text-mono { font-family: monospace; }
.empty { color: #64748b; text-align: center; padding: 30px; }

.admin-editor-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(4px);
}

.admin-editor-modal {
  display: flex;
  flex-direction: column;
  width: min(760px, 100%);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
}

.admin-editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.admin-editor-header h3 { margin: 0; color: #111827; font-size: 17px; }
.admin-editor-header p { margin: 5px 0 0; color: #64748b; font-size: 12px; }

.editor-close {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  padding: 0;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.admin-editor-body { padding: 22px 24px; overflow-y: auto; }
.business-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.editor-field { display: flex; flex-direction: column; gap: 7px; min-width: 0; }
.editor-field.full-width { grid-column: 1 / -1; }
.editor-field span { color: #475569; font-size: 12px; font-weight: 600; }
.editor-field input, .editor-field select, .editor-field textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  color: #111827;
  background: #fff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  outline: none;
  font: inherit;
  font-size: 13px;
}
.editor-field textarea { resize: vertical; }
.editor-field input:focus, .editor-field select:focus, .editor-field textarea:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.editor-error { margin-top: 16px; padding: 10px 12px; color: #b91c1c; background: #fef2f2; border-radius: 8px; font-size: 12px; }
.admin-editor-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #e5e7eb; }
.editor-secondary, .editor-primary { padding: 10px 15px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; }
.editor-secondary { color: #475569; background: #fff; border: 1px solid #dbe3ef; }
.editor-primary { color: #fff; background: #4f46e5; border: 1px solid #4f46e5; }
.editor-secondary:disabled, .editor-primary:disabled { opacity: .6; cursor: wait; }

.invoice-layout-overlay { padding: 14px; }
.invoice-layout-modal { width: min(1500px, 100%); height: calc(100vh - 28px); max-height: none; }
.invoice-layout-body { flex: 1; min-height: 0; overflow: hidden; }
.invoice-layout-body ::v-deep .invoice-template-picker { min-height: 0; height: 100%; }

@media (max-width: 640px) {
  .admin-editor-overlay { padding: 0; }
  .admin-editor-modal { width: 100%; height: 100%; max-height: none; border-radius: 0; }
  .business-form-grid { grid-template-columns: 1fr; }
  .editor-field.full-width { grid-column: auto; }
  .admin-editor-header, .admin-editor-body, .admin-editor-footer { padding-left: 16px; padding-right: 16px; }
}
</style>
